<template>
  <div class="maintenance-container">
    <div class="maintenance-card">
      <img src="@/assets/images/logo.png" alt="Logo" class="maintenance-logo" />
      <h1>Sitio en Mantenimiento</h1>
      <p>{{ maintenanceMessage || 'Estamos realizando mejoras en nuestro sistema. Disculpe las molestias.' }}</p>
      <p class="maintenance-message">Volveremos a estar en línea pronto.</p>
      
      <div class="maintenance-action">
        <button @click="goToLogin" class="btn btn-primary">
          Ingresar (administradores)
        </button>
        
        <button v-if="hasBypassPassword" @click="showBypassModal = true" class="btn btn-secondary">
          Ingresar con contraseña
        </button>
      </div>
    </div>
  </div>
  <div class="loader-container" v-if="loadingPage">
    <div class="loader"></div>
  </div>
  
  <!-- Modal para ingresar contraseña de bypass -->
  <BypassPasswordModal 
    :visible="showBypassModal" 
    @close="showBypassModal = false"
    @access-granted="handleAccessGranted"
  />
</template>

<script>
import { useRouter } from 'vue-router';
import BypassPasswordModal from '@/components/maintenance/BypassPasswordModal.vue';
import maintenanceService from '@/services/maintenanceService';

export default {
  name: 'MaintenancePage',
  components: {
    BypassPasswordModal
  },
  data() {
    return {
      loadingPage: true,
      showBypassModal: false,
      hasBypassPassword: false,
      maintenanceMessage: null
    };
  },
  setup() {
    const router = useRouter();

    const goToLogin = () => {
      router.push({ name: 'LoginForm' });
    };

    return {
      goToLogin
    };
  },
  methods: {
    async checkBypassPassword() {
      try {
        this.hasBypassPassword = await maintenanceService.hasBypassPassword();
        // Obtener el estado de mantenimiento (incluye el mensaje personalizado)
        const maintenanceStatus = await maintenanceService.getMaintenanceStatus();
        if (maintenanceStatus && maintenanceStatus.message) {
          this.maintenanceMessage = maintenanceStatus.message;
        }
      } catch (error) {
        console.error('Error checking bypass password:', error);
      }
    },
    async handleAccessGranted() {
      // Si el usuario ingresó la contraseña correctamente, redirigir a la página principal
      this.loadingPage = true;
      try {
        // Mostrar notificación de éxito con tiempo de validez
        this.$toast?.success('Acceso concedido. Bypass válido por 12 horas', {
          position: 'top-right',
          timeout: 5000
        });
        
        // Verificar si hay una ruta intentada previamente
        const intendedRoute = localStorage.getItem('intendedRoute');
        
        // Pequeño delay para permitir que la notificación sea visible
        await new Promise(resolve => setTimeout(resolve, 800));
        
        if (intendedRoute) {
          // Limpiar la ruta almacenada y redirigir a ella
          localStorage.removeItem('intendedRoute');
          console.log(`Redirigiendo a ruta intentada: ${intendedRoute}`);
          await this.$router.push(intendedRoute);
        } else {
          // Si no hay ruta intentada, ir a la página principal
          console.log('Redirigiendo a la página principal');
          await this.$router.push({ name: 'Home' });
        }
      } catch (error) {
        console.error('Error redirecting after bypass:', error);
        this.loadingPage = false;
        
        // Mostrar error si ocurre
        this.$toast?.error('Error al redirigir. Por favor intente nuevamente.', {
          position: 'top-right',
          timeout: 5000
        });
      }
    }
  },
  async mounted() {
    // Verificar si el sitio tiene contraseña de bypass configurada
    await this.checkBypassPassword();
    
    // Hide loader after a short delay
    setTimeout(() => {
      this.loadingPage = false;
    }, 500);
  }
};
</script>

<style scoped>
.maintenance-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 180px);
  padding: 2rem;
  background-color: var(--bg-color);
}

.maintenance-card {
  max-width: 600px;
  padding: 2.5rem;
  border-radius: 12px;
  background-color: var(--black-color-3);
  text-align: center;
}

.maintenance-logo {
  max-width: 180px;
  margin-bottom: 2rem;
}

.maintenance-icon {
  font-size: 4rem;
  margin-bottom: 1.5rem;
  color: var(--primary-color);
}

h1 {
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: var(--white-color);
}

p {
  font-size: 1.1rem;
  margin-bottom: 1rem;
  color: var(--white-color-2);
}

.maintenance-message {
  margin-top: 1.5rem;
  font-weight: 500;
}

.maintenance-action {
  margin-top: 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
}

.btn {
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 280px;
}

.btn-secondary {
  background-color: transparent;
  border: 1px solid var(--border-color);
  color: var(--white-color-2);
}

.btn-secondary:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

@media (max-width: 576px) {
  .maintenance-card {
    padding: 1.5rem;
  }

  h1 {
    font-size: 1.75rem;
  }

  p {
    font-size: 1rem;
  }
}

.loader-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loader {
  width: 50px;
  height: 50px;
  border: 3px solid var(--primary-color);
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>